"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { usePathname, useRouter } from "next/navigation";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import tourService from "../../services/tourService";
import { TourSpotlight } from "./TourSpotlight";
import { TourTooltip } from "./TourTooltip";
import { WelcomeTourModal } from "./WelcomeTourModal";
import { isDirect130Host } from "@/app/_module/utils/direct130Host";
import {
  TourContextType,
  TourDefinition,
  TourProgress,
  TourStep,
  TourContext as TourUserContext,
} from "../../config/tours/tour.types";
import { tourRegistry, INTERACTIVE_TOURS_ENABLED } from "../../config/tours";

// Create context with undefined default
const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: ReactNode;
}

/**
 * TourProvider Component
 * Manages tour state and renders spotlight + tooltip
 */
export function TourProvider({ children }: TourProviderProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useUserContext();

  // Ref to track if we're navigating to a chained tour
  const pendingNextTourRef = useRef<string | null>(null);
  // Ref to track recently completed/skipped tours to prevent immediate re-trigger
  const recentlyCompletedToursRef = useRef<Set<string>>(new Set());
  // Ref to track if welcome modal has been shown this session
  const welcomeShownRef = useRef(false);
  // Ref to track if all tours have been skipped (prevents any auto-start)
  const allToursSkippedRef = useRef(false);

  // Tour state
  const [activeTour, setActiveTour] = useState<TourDefinition | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<Record<string, TourProgress>>({});
  // Target rect for tooltip positioning (updates on scroll to follow element)
  const [tooltipTargetRect, setTooltipTargetRect] = useState<{
    top: number;
    left: number;
    width: number;
    height: number;
  } | null>(null);
  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Handler for spotlight rect updates - called on scroll/resize to keep tooltip in sync
  const handleTargetRect = useCallback((rect: { top: number; left: number; width: number; height: number } | null) => {
    if (rect) {
      setTooltipTargetRect(rect);
    }
  }, []);

  // Build user context for conditional steps
  const userContext: TourUserContext = useMemo(
    () => ({
      userType: (user?.user?.userType as any) || "agent",
      accountType: (user?.user?.accountType as any) || "basic",
      agencyRole: user?.currentAgency?.role as any,
      hasAgency: !!user?.currentAgency,
      isTrialUser: user?.user?.isTrialUser || false,
      isTrialActive: user?.user?.isTrialActive || user?.isTrialActive || false,
      isSystemAdmin: user?.user?.isSystemAdmin || false,
    }),
    [user]
  );

  // Get filtered steps (excluding skipped ones)
  const filteredSteps = useMemo(() => {
    if (!activeTour) return [];
    return activeTour.steps.filter(
      (step) => !step.skipIf || !step.skipIf(userContext)
    );
  }, [activeTour, userContext]);

  // Current step
  const currentStep = filteredSteps[currentStepIndex];

  // Load all tour progress on mount
  const refreshProgress = useCallback(async () => {
    if (!user?.user?.id) return;

    try {
      setIsLoading(true);
      setProgressLoaded(false); // Reset before loading
      const allProgress = await tourService.getAllTourProgress();
      const progressMap: Record<string, TourProgress> = {};
      allProgress.forEach((p) => {
        progressMap[p.tourId] = p;
      });
      setProgress(progressMap);

      // Check if the first tour (sidebar-navigation-tour) is skipped
      // If so, user has previously skipped all tours, so don't show any
      const sidebarProgress = progressMap['sidebar-navigation-tour'];
      if (sidebarProgress && sidebarProgress.status === 'skipped') {
        allToursSkippedRef.current = true;
      }

      // Mark as loaded after setting progress
      setProgressLoaded(true);
    } catch (error) {
      console.error("Failed to load tour progress:", error);
      // Still mark as loaded even on error, so tours can show for new users
      setProgressLoaded(true);
    } finally {
      setIsLoading(false);
    }
  }, [user?.user?.id]);

  // Load progress on mount
  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  // Check if tour should be shown based on user permissions
  const canShowTour = useCallback(
    (tour: TourDefinition): boolean => {
      // Check user type restriction
      if (
        tour.allowedUserTypes &&
        !tour.allowedUserTypes.includes(userContext.userType)
      ) {
        return false;
      }

      // Check account type restriction
      if (
        tour.allowedAccountTypes &&
        !tour.allowedAccountTypes.includes(userContext.accountType)
      ) {
        return false;
      }

      // Check agency requirement
      if (tour.requiresAgency && !userContext.hasAgency) {
        return false;
      }

      // Check agency role restriction
      if (
        tour.allowedAgencyRoles &&
        (!userContext.agencyRole ||
          !tour.allowedAgencyRoles.includes(userContext.agencyRole))
      ) {
        return false;
      }

      // Check system admin requirement
      if (tour.requiresSystemAdmin && !userContext.isSystemAdmin) {
        return false;
      }

      return true;
    },
    [userContext]
  );

  // Check if a tour should be shown (not completed/skipped, correct version)
  const shouldShowTour = useCallback(
    (tourId: string): boolean => {
      const tour = tourRegistry[tourId];
      if (!tour) return false;

      // Check if recently completed (prevents race condition)
      if (recentlyCompletedToursRef.current.has(tourId)) {
        return false;
      }

      // Check permissions
      if (!canShowTour(tour)) return false;

      // Check progress
      const tourProgress = progress[tourId];
      if (!tourProgress) return true; // Never started

      // Show if version changed
      if (tourProgress.version < tour.version) return true;

      // Don't show if completed or skipped
      if (
        tourProgress.status === "completed" ||
        tourProgress.status === "skipped"
      ) {
        return false;
      }

      return true;
    },
    [progress, canShowTour]
  );

  // Get progress for a specific tour
  const getProgress = useCallback(
    (tourId: string): TourProgress | undefined => {
      return progress[tourId];
    },
    [progress]
  );

  // Start a tour - navigates to the correct page first if needed
  const startTour = useCallback(
    async (tourId: string) => {
      if (!INTERACTIVE_TOURS_ENABLED) return; // tours are off; the welcome video is the onboarding
      const tour = tourRegistry[tourId];
      if (!tour) {
        console.error(`Tour not found: ${tourId}`);
        return;
      }

      if (!canShowTour(tour)) {
        console.warn(`User not allowed to view tour: ${tourId}`);
        return;
      }

      // Check if we're on the correct page for this tour
      // If not, navigate first and then start the tour
      if (pathname !== tour.page) {
        // Store the tour ID to start after navigation
        pendingNextTourRef.current = tourId;
        router.push(tour.page);
        return; // The tour will be started by the useEffect that watches for pending tours
      }

      try {
        setIsLoading(true);
        const tourProgress = await tourService.startTour(tourId, tour.version);
        setProgress((prev) => ({ ...prev, [tourId]: tourProgress }));
        setActiveTour(tour);

        // Find first valid step (skip steps with missing targets)
        // Use small timeout to let DOM render before checking for elements
        setTimeout(() => {
          const steps = tour.steps.filter(
            (step) => !step.skipIf || !step.skipIf(userContext)
          );
          let firstValidIndex = 0;
          for (let i = 0; i < steps.length; i++) {
            const element = document.querySelector(steps[i].targetSelector);
            if (element) {
              firstValidIndex = i;
              break;
            }
          }
          setCurrentStepIndex(firstValidIndex);
          setIsVisible(true);

          // Call onShow hook for the first step
          setTimeout(() => {
            steps[firstValidIndex]?.onShow?.();
          }, 100);
        }, 100);
      } catch (error) {
        console.error("Failed to start tour:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [canShowTour, userContext, pathname, router]
  );

  // Find the next tour in chain that the user can view
  const findNextViewableTour = useCallback((startTourId: string | undefined): TourDefinition | null => {
    let currentId = startTourId;
    const visited = new Set<string>(); // Prevent infinite loops

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const tour = tourRegistry[currentId];
      if (!tour) return null;

      // Check if user can view this tour
      if (canShowTour(tour)) {
        // Check if not already completed/skipped
        const tourProgress = progress[currentId];
        const shouldShow = !tourProgress ||
          tourProgress.version < tour.version ||
          (tourProgress.status !== "completed" && tourProgress.status !== "skipped");

        if (shouldShow) {
          return tour;
        }
      }

      // Move to next tour in chain
      currentId = tour.nextTourId;
    }

    return null;
  }, [canShowTour, progress]);

  // Skip/dismiss current tour only - marks as skipped, won't show again, chains to next tour
  const skipTour = useCallback(async () => {
    if (!activeTour) return;

    const tourId = activeTour.id;
    const tourVersion = activeTour.version;
    const nextTourId = activeTour.nextTourId;

    // Mark as recently completed to prevent re-trigger
    recentlyCompletedToursRef.current.add(tourId);

    // Immediately update local state to prevent any flashing
    setProgress((prev) => ({
      ...prev,
      [tourId]: {
        id: prev[tourId]?.id || `temp-${tourId}`,
        userId: user?.user?.id || '',
        tourId,
        version: tourVersion,
        currentStep: 0,
        status: 'skipped' as const,
        skippedAt: new Date().toISOString(),
        createdAt: prev[tourId]?.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    }));

    try {
      // Pass the tour version to ensure it stays skipped even if tour versions increase
      const tourProgress = await tourService.skipTour(tourId, tourVersion);
      if (tourProgress) {
        setProgress((prev) => ({ ...prev, [tourId]: tourProgress }));
      }
    } catch (error) {
      console.error("Failed to skip tour:", error);
    }

    setActiveTour(null);
    setCurrentStepIndex(0);
    setIsVisible(false);
    setTooltipTargetRect(null);

    // Chain to next tour (skip only skips THIS tour, continues with chain)
    const nextViewableTour = findNextViewableTour(nextTourId);
    if (nextViewableTour) {
      pendingNextTourRef.current = nextViewableTour.id;
      // Navigate to the next tour's page
      router.push(nextViewableTour.page);
    }
  }, [activeTour, findNextViewableTour, router, user?.user?.id]);

  // Complete tour and optionally navigate to next tour
  const completeTour = useCallback(async () => {
    if (!activeTour) return;

    const tourId = activeTour.id;

    // Mark as recently completed to prevent re-trigger
    recentlyCompletedToursRef.current.add(tourId);

    try {
      const tourProgress = await tourService.completeTour(tourId);
      if (tourProgress) {
        setProgress((prev) => ({ ...prev, [tourId]: tourProgress }));
      }
    } catch (error) {
      console.error("Failed to complete tour:", error);
    }

    setActiveTour(null);
    setCurrentStepIndex(0);
    setIsVisible(false);
    setTooltipTargetRect(null);

    // Find the next viewable tour in the chain (skips tours user can't see)
    const nextViewableTour = findNextViewableTour(activeTour.nextTourId);

    if (nextViewableTour) {
      pendingNextTourRef.current = nextViewableTour.id;
      // Navigate to the next tour's page
      router.push(nextViewableTour.page);
    }
  }, [activeTour, findNextViewableTour, router]);

  // Find next valid step index (skipping steps with missing target elements)
  const findNextValidStepIndex = useCallback((startIndex: number): number => {
    for (let i = startIndex; i < filteredSteps.length; i++) {
      const step = filteredSteps[i];
      const element = document.querySelector(step.targetSelector);
      if (element) {
        return i;
      }
    }
    return -1; // No valid step found
  }, [filteredSteps]);

  // Go to next step
  const nextStep = useCallback(async () => {
    if (!activeTour) return;

    // Find next valid step (skip steps with missing targets)
    const nextIndex = findNextValidStepIndex(currentStepIndex + 1);

    if (nextIndex === -1) {
      // No more valid steps, complete the tour
      completeTour();
      return;
    }

    // Call onNext hook if defined
    currentStep?.onNext?.();

    // Call onShow hook BEFORE changing step - this allows the hook to prepare the UI
    // (e.g., open a minimized dialer) before the spotlight tries to find the element
    filteredSteps[nextIndex]?.onShow?.();

    // Update step on server
    try {
      await tourService.updateTourStep(activeTour.id, nextIndex);
    } catch (error) {
      console.error("Failed to update step:", error);
    }

    // Reset tooltip rect flag so new position can be set
    setTooltipTargetRect(null);

    // Small delay to allow UI to update after onShow before changing step
    setTimeout(() => {
      setCurrentStepIndex(nextIndex);
    }, 50);
  }, [activeTour, currentStepIndex, filteredSteps, currentStep, findNextValidStepIndex, completeTour]);

  // Go to previous step
  const previousStep = useCallback(async () => {
    if (!activeTour || currentStepIndex <= 0) return;

    const prevIndex = currentStepIndex - 1;

    // Call onShow hook BEFORE changing step - this allows the hook to prepare the UI
    filteredSteps[prevIndex]?.onShow?.();

    try {
      await tourService.updateTourStep(activeTour.id, prevIndex);
    } catch (error) {
      console.error("Failed to update step:", error);
    }

    // Reset tooltip rect flag so new position can be set
    setTooltipTargetRect(null);

    // Small delay to allow UI to update after onShow before changing step
    setTimeout(() => {
      setCurrentStepIndex(prevIndex);
    }, 50);
  }, [activeTour, currentStepIndex, filteredSteps]);

  // Reset tour for replay
  const resetTour = useCallback(
    async (tourId: string) => {
      try {
        await tourService.resetTour(tourId);
        setProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[tourId];
          return newProgress;
        });
        // Clear from recently completed so it can show again
        recentlyCompletedToursRef.current.delete(tourId);
      } catch (error) {
        console.error("Failed to reset tour:", error);
      }
    },
    []
  );

  // Reset all tours for replay
  const resetAllTours = useCallback(async () => {
    try {
      await tourService.resetAllTours();
      setProgress({});
      // Clear all recently completed tours so they can show again
      recentlyCompletedToursRef.current.clear();
      // Reset welcome modal flag so it can show again
      welcomeShownRef.current = false;
      // Reset the all-tours-skipped flag so tours can auto-start again
      allToursSkippedRef.current = false;
    } catch (error) {
      console.error("Failed to reset all tours:", error);
    }
  }, []);

  // Track if initial progress load is complete
  const [progressLoaded, setProgressLoaded] = useState(false);

  // Handle pending tour after navigation (separate from auto-start)
  // This effect runs when pathname changes to start a pending tour
  useEffect(() => {
    if (!pathname || !user?.user?.id || activeTour || isLoading) return;

    // Check if we have a pending chained tour to start (from navigation)
    if (pendingNextTourRef.current) {
      const pendingTourId = pendingNextTourRef.current;
      const pendingTour = tourRegistry[pendingTourId];

      // Only start if we're on the correct page for this tour
      if (pendingTour && pendingTour.page === pathname) {
        pendingNextTourRef.current = null; // Clear the pending tour
        // Delay to let page fully render before starting tour
        const timer = setTimeout(() => {
          startTour(pendingTourId);
        }, 500); // Longer delay for page to fully load
        return () => clearTimeout(timer);
      }
    }
  }, [pathname, user?.user?.id, activeTour, isLoading, startTour]);

  // Auto-start tour for current page if applicable
  useEffect(() => {
    // Wait for progress to be loaded before auto-starting tours
    if (!pathname || !user?.user?.id || isLoading || activeTour || !progressLoaded) return;

    // Skip if we have a pending tour (handled by the other useEffect)
    if (pendingNextTourRef.current) return;

    // Don't auto-start any tours if user has skipped all tours
    if (allToursSkippedRef.current) {
      return;
    }

    // Also check if sidebar-navigation-tour is skipped in the progress state directly
    // This is a fallback in case the ref wasn't set properly
    const sidebarProgress = progress['sidebar-navigation-tour'];
    if (sidebarProgress && sidebarProgress.status === 'skipped') {
      allToursSkippedRef.current = true;
      return;
    }

    // Find tour for current page
    // Priority order for dashboard: sidebar-navigation-tour > navbar-tour > dashboard-tour
    const pageTours = Object.values(tourRegistry).filter(
      (tour) => tour.page === pathname
    );

    // Sort tours by priority (sidebar first for dashboard page)
    const tourPriority: Record<string, number> = {
      'sidebar-navigation-tour': 1,
      'navbar-tour': 2,
      'dashboard-tour': 3,
    };

    pageTours.sort((a, b) => {
      const priorityA = tourPriority[a.id] || 100;
      const priorityB = tourPriority[b.id] || 100;
      return priorityA - priorityB;
    });

    // Find the first tour that should be shown
    const pageTour = pageTours.find((tour) => {
      // Skip if recently completed
      if (recentlyCompletedToursRef.current.has(tour.id)) {
        return false;
      }
      return shouldShowTour(tour.id);
    });

    // Tours off: the only thing that ever opens is the welcome modal, once,
    // on the dashboard, for an account with no tour record yet.
    if (pageTour && !INTERACTIVE_TOURS_ENABLED && pageTour.id !== 'sidebar-navigation-tour') return;

    if (pageTour) {
      // If this is the sidebar navigation tour (first tour for new users),
      // show the welcome modal instead of starting directly
      if (pageTour.id === 'sidebar-navigation-tour' && !welcomeShownRef.current) {
        welcomeShownRef.current = true;
        // A blocking agency announcement takes priority over the walkthrough —
        // firing both stacked the welcome card on top of the announcement
        // modal. Wait for the modal to broadcast "announcements-cleared".
        // Same for the Terms re-acceptance gate: it announces itself with a
        // root attribute + "ic-terms-gate" event (TermsReacceptanceGate).
        // Both stacked on the welcome card at once for a new account.
        const show = () => {
          // The welcome card is InsuraCentral's (its logo and demo video): not on Direct 130's address.
          if (isDirect130Host()) return;
          if (document.documentElement.hasAttribute("data-terms-gate")) {
            const onGate = (e: Event) => {
              if ((e as CustomEvent).detail?.required === false) {
                window.removeEventListener("ic-terms-gate", onGate);
                show();
              }
            };
            window.addEventListener("ic-terms-gate", onGate);
            return;
          }
          if (document.body.dataset.blockingAnnouncements === "true") {
            window.addEventListener("announcements-cleared", show, { once: true });
            return;
          }
          setShowWelcomeModal(true);
        };
        const timer = setTimeout(show, 1500); // Slightly longer delay for welcome modal
        return () => {
          clearTimeout(timer);
          window.removeEventListener("announcements-cleared", show);
        };
      }

      // Small delay to let page render (same announcement-priority guard —
      // a tour spotlight over the blocking modal is equally broken).
      const start = () => startTour(pageTour.id);
      const timer = setTimeout(() => {
        if (document.body.dataset.blockingAnnouncements === "true") {
          window.addEventListener("announcements-cleared", start, { once: true });
        } else {
          start();
        }
      }, 200);

      return () => {
        clearTimeout(timer);
        window.removeEventListener("announcements-cleared", start);
      };
    }
  }, [pathname, user?.user?.id, isLoading, activeTour, shouldShowTour, startTour, progressLoaded, progress]);

  // Context value
  const contextValue: TourContextType = useMemo(
    () => ({
      activeTour,
      currentStepIndex,
      isVisible,
      isLoading,
      progress,
      startTour,
      nextStep,
      previousStep,
      skipTour,
      completeTour,
      resetTour,
      resetAllTours,
      shouldShowTour,
      getProgress,
      refreshProgress,
    }),
    [
      activeTour,
      currentStepIndex,
      isVisible,
      isLoading,
      progress,
      startTour,
      nextStep,
      previousStep,
      skipTour,
      completeTour,
      resetTour,
      resetAllTours,
      shouldShowTour,
      getProgress,
      refreshProgress,
    ]
  );

  // Marks every tour skipped (local + server) so the welcome modal never
  // re-opens. Shared by Skip and, with tours off, by "Jump in" after the video.
  const markAllToursDone = useCallback(async () => {

    // Set flag to prevent any tours from auto-starting for this session
    allToursSkippedRef.current = true;

    // Skip ALL tours - mark all as skipped so none show again
    const allTourIds = Object.keys(tourRegistry);

    // Immediately mark all tours as skipped in local state to prevent any from showing
    // This creates synthetic progress records with 'skipped' status
    setProgress((prev) => {
      const newProgress = { ...prev };
      allTourIds.forEach((tourId) => {
        const tour = tourRegistry[tourId];
        newProgress[tourId] = {
          id: `temp-${tourId}`,
          userId: user?.user?.id || '',
          tourId,
          version: tour?.version || 1,
          currentStep: 0,
          status: 'skipped' as const,
          skippedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
      });
      return newProgress;
    });

    // Also add to recently completed ref as backup
    for (const tourId of allTourIds) {
      recentlyCompletedToursRef.current.add(tourId);
    }

    try {
      // Skip all tours in the database with their current version
      await Promise.all(
        allTourIds.map(async (tourId) => {
          const tour = tourRegistry[tourId];
          await tourService.skipTour(tourId, tour?.version || 999);
        })
      );
      // Refresh progress from server to get actual records
      await refreshProgress();
    } catch (error) {
      console.error('Failed to skip all tours:', error);
    }
  }, [user?.user?.id, refreshProgress]);

  return (
    <TourContext.Provider value={contextValue}>
      {children}

      {/* Welcome modal for first-time users */}
      <WelcomeTourModal
        isOpen={showWelcomeModal}
        userName={user?.user?.name || 'there'}
        toursEnabled={INTERACTIVE_TOURS_ENABLED}
        onStartTour={() => {
          setShowWelcomeModal(false);
          if (!INTERACTIVE_TOURS_ENABLED) {
            void markAllToursDone();
            return;
          }
          // Start the sidebar navigation tour
          startTour('sidebar-navigation-tour');
        }}
        onSkip={async () => {
          setShowWelcomeModal(false);
          await markAllToursDone();
        }}
      />

      {/* Render tour UI */}
      {isVisible && currentStep && (
        <>
          <TourSpotlight
            targetSelector={currentStep.targetSelector}
            stepIndex={currentStepIndex}
            padding={currentStep.spotlightPadding}
            isVisible={isVisible}
            onTargetRect={handleTargetRect}
          />
          <TourTooltip
            title={currentStep.title}
            description={currentStep.description}
            currentStep={currentStepIndex + 1}
            totalSteps={filteredSteps.length}
            placement={currentStep.placement}
            targetRect={tooltipTargetRect}
            isVisible={isVisible}
            hasPrevious={currentStepIndex > 0}
            hasNext={currentStepIndex < filteredSteps.length - 1}
            onNext={nextStep}
            onPrevious={previousStep}
            onSkip={skipTour}
            onComplete={completeTour}
          />
        </>
      )}
    </TourContext.Provider>
  );
}

/**
 * Hook to access tour context
 */
export function useTour(): TourContextType {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error("useTour must be used within a TourProvider");
  }
  return context;
}

export default TourProvider;
